import { Stack, Switch, Flex, Spinner, Text, Divider, Button } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUserStore } from '@store/app.store';
import { useSetDocumentTitle, useStyledToast, useInvalidateQueries } from '@util/hooks';

import SettingsLayout from '@layouts/settings.layout';
import ZoomPersonPreview from '@components/general/avatar-preview/zoom-person-preview.component';

import {
  useZoomUsers,
  useZoomAccountSettings,
  usePatchZoomAccountSettings
} from '@services/query-services/lambda.query-service';

export default function ZoomSettingsPage() {
  useSetDocumentTitle('Zoom Admin - FORA');
  const tenant = useUserStore((state) => state?.tenant);
  const navigate = useNavigate();
  const toast = useStyledToast();
  const invalidateQueries = useInvalidateQueries();
  const [pageToken, setPageToken] = useState(null);

  const { data: accountSettings, isLoading: isLoadingSettings } = useZoomAccountSettings(
    tenant?.recall_config?.zoom_oauth_credential_id
  );
  const { data: zoomUsersData, isLoading } = useZoomUsers(
    tenant?.recall_config?.zoom_oauth_credential_id,
    pageToken
  );

  const { mutateAsync: patchAccountSettings } = usePatchZoomAccountSettings();
  useEffect(() => {
    if (tenant && !tenant?.recall_config?.zoom_oauth_credential_id) {
      navigate('/settings/sources');
      toast({
        title: 'Zoom account not connected. Zoom must be authorized first',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    }
  }, [tenant]);

  const handleSwitchChange = async (event) => {
    await patchAccountSettings(
      {
        zoom_oauth_credential_id: tenant?.recall_config?.zoom_oauth_credential_id,
        payload: {
          recording: {
            local_recording: event.target.checked
          }
        }
      },
      {
        onSuccess: () => {
          toast({
            title: 'Account updated successfully',
            status: 'success',
            duration: 9000,
            isClosable: true
          });
          invalidateQueries({
            model: 'lambda',
            queryKey: [
              'GET',
              'zoom/account-settings',
              undefined,
              {
                zoom_oauth_credential_id: tenant?.recall_config?.zoom_oauth_credential_id
              }
            ]
          });
        }
      }
    );
  };

  const handleNextPage = () => {
    if (zoomUsersData?.next_page_token) {
      setPageToken(zoomUsersData.next_page_token);
    }
  };

  const handlePreviousPage = () => {
    setPageToken(null);
  };

  return (
    <SettingsLayout
      w='3xl'
      mx='auto'
    >
      <Stack gap={6}>
        <Flex
          gap={2.5}
          size='sm'
          p={3}
          border='reskin'
          align='flex-start'
          borderRadius='xl'
        >
          <Text
            color='brandPrimary.primary'
            fontWeight={600}
            letterSpacing='tight'
            fontSize='sm'
            lineHeight='16px'
            w='50%'
          >
            Account Settings
          </Text>
          {isLoadingSettings ? (
            <Spinner
              size='lg'
              data-testid='settings-loading-spinner'
            />
          ) : (
            <Flex
              w='100%'
              align='flex-start'
              justifyContent='flex-end'
              alignSelf='flex-end'
            >
              <Switch
                isChecked={accountSettings?.recording?.local_recording}
                onChange={handleSwitchChange}
                p={2}
              />
              <Stack align='flex-start'>
                <Text
                  fontSize='sm'
                  variant='page-header'
                >
                  Local Recording
                </Text>
                <Text
                  fontWeight={500}
                  color='surface.onSurfaceVariant'
                  fontSize='xs'
                >
                  FORA requires local recording be enabled
                </Text>
              </Stack>
            </Flex>
          )}
        </Flex>
        <Divider color='surface.outline' />
        {isLoading ? (
          <Spinner
            size='lg'
            data-testid='users-loading-spinner'
          />
        ) : (
          <>
            {zoomUsersData?.users?.length > 0 ? (
              <>
                <Text
                  color='surface.onSurfaceVariant'
                  fontSize='sm'
                >
                  Showing {zoomUsersData.users.length} of {zoomUsersData.total_records} users
                </Text>
                {zoomUsersData.users.map((user) => (
                  <ZoomPersonPreview
                    key={user?.email}
                    user={user}
                  />
                ))}
                <Flex
                  gap={3}
                  justify='space-between'
                  align='center'
                >
                  <Button
                    onClick={handlePreviousPage}
                    isDisabled={!pageToken}
                    size='sm'
                  >
                    Previous Page
                  </Button>
                  <Button
                    onClick={handleNextPage}
                    isDisabled={!zoomUsersData?.next_page_token}
                    size='sm'
                  >
                    Next Page
                  </Button>
                </Flex>
              </>
            ) : (
              <Text color='surface.onSurfaceVariant'>No users found.</Text>
            )}
          </>
        )}
      </Stack>
    </SettingsLayout>
  );
}
